import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Loader2, Trophy, Clock, CheckCircle2, XCircle, Users, TrendingUp } from "lucide-react";

const RANGES = [
  { v: "7d", l: "Last 7 days" },
  { v: "30d", l: "Last 30 days" },
  { v: "90d", l: "Last 90 days" },
  { v: "all", l: "All time" },
];

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function initials(name) {
  if (!name) return "?";
  return name.split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();
}

function colorForName(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
}

export default function LiveSupportAgentPerformance() {
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ leaderboard: [], totals: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/live-support/agent-performance?range=${range}`);
      setData({ leaderboard: data?.leaderboard || [], totals: data?.totals || {} });
    } catch (_) {
      setData({ leaderboard: [], totals: {} });
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const top = data.leaderboard[0];
  const totals = data.totals || {};

  return (
    <div className="space-y-4">
      <PageHeader
        title="🏆 Agent Performance Leaderboard"
        subtitle="Per-agent ticket resolution metrics & top-form breakdown"
      />

      {/* Range filter */}
      <div className="bg-white border border-gray-200 rounded-sm px-4 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setRange(r.v)} data-testid={`ls-perf-range-${r.v}`}
                    className={`h-8 px-3 rounded-sm text-xs font-bold border transition-colors ${
                      range === r.v
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}>{r.l}</button>
          ))}
        </div>
        <button onClick={load} disabled={loading}
                className="h-8 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-1.5">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
          Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<Users size={16} />} label="Active Agents" value={totals.agents || 0} accent="emerald" />
        <KpiCard icon={<CheckCircle2 size={16} />} label="Total Resolved" value={totals.resolved || 0} accent="emerald" />
        <KpiCard icon={<XCircle size={16} />} label="Total Rejected" value={totals.rejected || 0} accent="red" />
        <KpiCard icon={<Clock size={16} />} label="Avg Resolution Time" value={fmtDuration(totals.avg_resolution_seconds)} accent="blue" />
      </div>

      {/* Top performer highlight */}
      {top && (
        <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-white border border-amber-200 rounded-sm p-4 flex items-center gap-4">
          <div className="h-14 w-14 rounded-full text-white font-black text-xl flex items-center justify-center shrink-0 shadow-lg"
               style={{ background: `linear-gradient(135deg, hsl(${colorForName(top.name)} 75% 50%), hsl(${colorForName(top.name)} 75% 35%))` }}>
            {initials(top.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Trophy size={14} className="text-amber-500" />
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-700">Top Performer</span>
            </div>
            <div className="font-extrabold text-gray-900 text-lg truncate">{top.name}</div>
            <div className="text-[12px] text-gray-600">
              <span className="font-bold text-emerald-700">{top.resolved}</span> tickets resolved
              {top.avg_resolution_seconds > 0 && (
                <> · avg <span className="font-bold text-blue-700">{fmtDuration(top.avg_resolution_seconds)}</span></>
              )}
              {top.top_forms[0] && (
                <> · most-handled: <span className="font-semibold text-gray-800">{top.top_forms[0].label}</span></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ls-perf-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-600 font-bold">
                <th className="px-3 py-2.5 w-12">#</th>
                <th className="px-3 py-2.5">Agent</th>
                <th className="px-3 py-2.5 text-center">Resolved</th>
                <th className="px-3 py-2.5 text-center">Rejected</th>
                <th className="px-3 py-2.5 text-center">Total Handled</th>
                <th className="px-3 py-2.5 text-center">Avg Resolution</th>
                <th className="px-3 py-2.5">Top Forms</th>
                <th className="px-3 py-2.5">Last Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500">
                  <Loader2 size={14} className="animate-spin inline mr-2" />Loading…
                </td></tr>
              ) : data.leaderboard.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-gray-500">
                  এই সময়ের মধ্যে কোনো এজেন্টের activity নেই।
                </td></tr>
              ) : data.leaderboard.map((a, i) => (
                <tr key={a.name} className="border-b border-gray-100 hover:bg-emerald-50/30" data-testid={`ls-perf-row-${i}`}>
                  <td className="px-3 py-2.5 font-black text-gray-500">
                    {i === 0 ? <span className="text-amber-500 text-lg">🥇</span>
                     : i === 1 ? <span className="text-gray-400 text-lg">🥈</span>
                     : i === 2 ? <span className="text-orange-400 text-lg">🥉</span>
                     : <span className="text-gray-400">{i + 1}</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-full text-white font-black text-[11px] flex items-center justify-center ring-1 ring-white/40 shrink-0"
                           style={{ background: `linear-gradient(135deg, hsl(${colorForName(a.name)} 75% 50%), hsl(${colorForName(a.name)} 75% 35%))` }}>
                        {initials(a.name)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900 truncate">{a.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-[12px] font-black bg-emerald-50 text-emerald-700 border border-emerald-200">{a.resolved}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {a.rejected > 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-[12px] font-black bg-red-50 text-red-700 border border-red-200">{a.rejected}</span>
                    ) : <span className="text-gray-300">0</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center font-bold text-gray-700">{a.total}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="font-mono text-[12px] text-blue-700 font-semibold">{fmtDuration(a.avg_resolution_seconds)}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {a.top_forms.slice(0, 3).map((f, fi) => (
                        <span key={fi} className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                          {f.label} · {f.count}
                        </span>
                      ))}
                      {a.top_forms.length === 0 && <span className="text-gray-300">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-gray-500">
                    {a.last_action_at ? new Date(a.last_action_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  const map = {
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    red: "from-red-50 to-white border-red-200 text-red-700",
    blue: "from-blue-50 to-white border-blue-200 text-blue-700",
  };
  return (
    <div className={`bg-gradient-to-br ${map[accent] || map.emerald} border rounded-sm p-3.5`}>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <span className="text-[10px] font-black uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-black text-gray-900 leading-none">{value}</div>
    </div>
  );
}
